import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import type {
  CustomFieldRecord,
  CustomFieldService,
} from "@/shared/domain/customFields";
import type { ResourceIdQueryService } from "@/shared/domain/query";

import {
  collectionIdSuggestions,
  customFieldValueSuggestions,
  datasetIdSuggestions,
  deviceIdSuggestions,
  sessionIdSuggestions,
} from "./suggestionProviders";
import type { SuggestionsProvider } from "./suggestions";

/** The id suggestion providers for one org, one per id-bearing entity. */
export interface IdSuggestions {
  device: SuggestionsProvider;
  session: SuggestionsProvider;
  dataset: SuggestionsProvider;
  collection: SuggestionsProvider;
}

type DomainServices = ReturnType<typeof useDomainServices>;

/**
 * The query service, read only when a provider actually searches. The domain
 * context resolves services through throwing getters, so a page whose user
 * never opens an id filter never touches it — and a test that doesn't stub it
 * doesn't have to.
 */
function lazyQueryService(services: DomainServices): ResourceIdQueryService {
  return {
    submitStructured: (...args) =>
      services.queryService.submitStructured(...args),
    getQueryResults: (...args) =>
      services.queryService.getQueryResults(...args),
  };
}

/**
 * Id suggestions for `orgId`'s devices, sessions, datasets and collections, to
 * decorate the id rows of a filter picker. `undefined` without an org, which
 * leaves those rows without suggestions. Nothing is fetched until a field
 * asks.
 */
export function useIdSuggestions(
  orgId: string | undefined,
): IdSuggestions | undefined {
  const services = useDomainServices();
  return React.useMemo(() => {
    if (orgId === undefined) {
      return undefined;
    }
    const queryService = lazyQueryService(services);
    return {
      device: deviceIdSuggestions(queryService, orgId),
      session: sessionIdSuggestions(queryService, orgId),
      dataset: datasetIdSuggestions(queryService, orgId),
      collection: collectionIdSuggestions(queryService, orgId),
    };
  }, [services, orgId]);
}

/**
 * Builds a String custom field's value suggestions, for
 * `buildCustomFieldFilterDefinitions`' `suggestionsFor`. Each field carries
 * its own owning org.
 */
export function useCustomFieldValueSuggestions(): (
  field: CustomFieldRecord,
) => SuggestionsProvider {
  const services = useDomainServices();
  return React.useMemo(() => {
    const customFieldService: Pick<CustomFieldService, "getUniqueValues"> = {
      getUniqueValues: (...args) =>
        services.customFieldService.getUniqueValues(...args),
    };
    return (field: CustomFieldRecord) =>
      customFieldValueSuggestions(customFieldService, {
        fieldId: field.field_id,
        orgId: field.org_id,
        entityType: field.entity_type,
      });
  }, [services]);
}
